import { useEffect, useState } from 'react'
import {
  getApiClient,
  type GiftSummaryCallsResponse,
} from '@/lib/api'
import {
  AsOfLabel,
  ErrorRow,
  fmtInt,
  fmtPct,
  KpiCard,
  LoadingRow,
  PageHeader,
  Section,
  StatusNotice,
  TogglePill,
} from './shared'

function DailyChart({
  daily,
}: {
  daily: Array<{ day: string; calls: number; apo: number; isHoliday: boolean }>
}) {
  if (daily.length === 0) return null
  const width = 720
  const height = 200
  const padLeft = 36
  const padBottom = 22
  const padTop = 8
  const innerW = width - padLeft - 8
  const innerH = height - padTop - padBottom
  const maxCalls = Math.max(1, ...daily.map((d) => d.calls))
  const step = innerW / daily.length
  const barW = Math.max(2, step * 0.5)
  const y = (v: number) => padTop + innerH - (v / maxCalls) * innerH
  const apoMax = Math.max(1, ...daily.map((d) => d.apo))
  const apoY = (v: number) => padTop + innerH - (v / apoMax) * innerH

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="今月の日別コール数とアポ数"
      className="w-full"
    >
      {/* コール棒 */}
      {daily.map((d, i) => (
        <rect
          key={d.day}
          x={padLeft + i * step + (step - barW) / 2}
          y={y(d.calls)}
          width={barW}
          height={Math.max(0, padTop + innerH - y(d.calls))}
          fill={d.isHoliday ? '#e2e8f0' : '#3b82f6'}
        >
          <title>{`${d.day} コール ${d.calls}件`}</title>
        </rect>
      ))}
      {/* アポ折れ線 */}
      <polyline
        fill="none"
        stroke="#f59e0b"
        strokeWidth="2"
        points={daily
          .map((d, i) => `${padLeft + i * step + step / 2},${apoY(d.apo)}`)
          .join(' ')}
      />
      {daily.map((d, i) => (
        <circle
          key={`apo-${d.day}`}
          cx={padLeft + i * step + step / 2}
          cy={apoY(d.apo)}
          r="2.5"
          fill="#f59e0b"
        >
          <title>{`${d.day} アポ ${d.apo}件`}</title>
        </circle>
      ))}
      {/* 目盛 */}
      <text x={padLeft - 6} y={padTop + 10} textAnchor="end" fontSize="10" fill="#94a3b8">
        {maxCalls}
      </text>
      <text x={padLeft - 6} y={padTop + innerH + 4} textAnchor="end" fontSize="10" fill="#94a3b8">
        0
      </text>
    </svg>
  )
}

export function SummaryCallsPage() {
  const [data, setData] = useState<GiftSummaryCallsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [period, setPeriod] = useState<'yesterday' | 'month'>('month')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void getApiClient()
      .getGiftSummaryCalls()
      .then((result) => {
        if (!cancelled) {
          setData(result)
          setError(false)
          setLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true)
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const kpi = period === 'yesterday' ? data?.latestWorkday : data?.monthKpi
  const kpiLabel = period === 'yesterday' ? `昨日（直近稼働日）` : `今月（${data?.month ?? ''}）`

  return (
    <div className="space-y-6">
      <PageHeader title="コール" sub="AX テレアポコールの日次・今月 KPI" />

      {loading ? (
        <LoadingRow label="コール集計" />
      ) : error ? (
        <ErrorRow label="コール集計" />
      ) : data && data.dataStatus === 'not_connected' ? (
        <StatusNotice
          dataStatus="not_connected"
          note="AX_SOURCE_SCOPE と日次集計（cb_daily_metric_counts）が接続されると表示されます。"
        />
      ) : (
        <>
          <div className="flex items-center gap-2">
            <TogglePill active={period === 'yesterday'} onClick={() => setPeriod('yesterday')}>
              昨日（直近稼働日）
            </TogglePill>
            <TogglePill active={period === 'month'} onClick={() => setPeriod('month')}>
              今月
            </TogglePill>
            <span className="ml-auto">
              <AsOfLabel computedAt={data?.computedAt ?? null} />
            </span>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard label={`${kpiLabel} コール`} value={fmtInt(kpi?.calls)} sub="コール件数" />
            <KpiCard label={`${kpiLabel} 接続`} value={fmtInt(kpi?.connected)} sub="人が出た通話" />
            <KpiCard label={`${kpiLabel} アポ`} value={fmtInt(kpi?.apo)} sub="アポ獲得件数" />
            <KpiCard
              label={`${kpiLabel} アポ率`}
              value={fmtPct(kpi?.apoRate)}
              sub={kpi?.smallDenominator ? '※母数少（20件未満）' : 'アポ ÷ コール'}
            />
          </div>

          <Section
            title="今月の日別推移"
            note="青 = コール件数（棒）、橙 = アポ件数（線）。祝休日は薄色で表示し率から除外"
          >
            {data?.daily && data.daily.length > 0 ? (
              <DailyChart daily={data.daily} />
            ) : (
              <StatusNotice dataStatus="empty" />
            )}
          </Section>

          <Section title="担当別（今月）" note="caller 軸の件数（コール降順）">
            {data && data.callers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="whitespace-nowrap py-2 pr-4">担当</th>
                      <th className="whitespace-nowrap px-2 py-2 text-right">コール</th>
                      <th className="whitespace-nowrap px-2 py-2 text-right">接続</th>
                      <th className="whitespace-nowrap px-2 py-2 text-right">アポ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.callers.map((r) => (
                      <tr key={r.caller} className="border-b last:border-0">
                        <td className="whitespace-nowrap py-2 pr-4 font-medium">{r.caller}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{fmtInt(r.calls)}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{fmtInt(r.connected)}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{fmtInt(r.apo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <StatusNotice dataStatus="empty" />
            )}
          </Section>

          <Section title="NG理由（今月・上位）" note="ng_reason 軸の件数">
            {data && data.ngReasons.length > 0 ? (
              <ol className="grid gap-1">
                {data.ngReasons.map((r, i) => (
                  <li
                    key={r.reason}
                    className="flex items-center gap-3 rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    <span className="font-semibold tabular-nums text-primary">{i + 1}</span>
                    <span className="flex-1">{r.reason}</span>
                    <span className="tabular-nums text-muted-foreground">{fmtInt(r.count)}件</span>
                  </li>
                ))}
              </ol>
            ) : (
              <StatusNotice dataStatus="empty" />
            )}
          </Section>
        </>
      )}
    </div>
  )
}
